// src/pages/calidad/PpniPncFormulario.jsx
// Campos del formato F-CAL-05 (Seguimiento a PPNI y PNC). Lo usan la
// captura y la edición desde el reporte.
import { FiExternalLink } from 'react-icons/fi';

// Carpeta de SharePoint donde se guardan las imágenes de referencia (la misma liga del Excel)
export const LIGA_CARPETA_IMAGENES = 'https://dasavenagourmet123.sharepoint.com/:i:/s/SistemadeGestion/IQDQgCPNKxlCTLVYiUVwOGJgAb3y4mlLUcAahb0e5vf0U7g?e=yb4IHM';

export const VACIO = {
  fecha_deteccion: '', clasificacion: '', tipo_producto: '', tipo_desviacion: '',
  pertenece_pcc: '', cual_pcc: '', peligro_fisico: '',
  producto: '', proveedor: '', lote: '', area_proceso: '',
  kg_desviacion: '', total_muestra: '', descripcion: '',
  numero_netsuite: '', quien_captura: '', turno: '',
  imagen_nombre: '', imagen_link: '', disposicion: '', firma_disposicion: '',
};

const pad = (n, l = 2) => String(n).padStart(l, '0');

/** Nombre sugerido de la imagen según el criterio del formato: día del año + año + PPNI/PNC (ej. 2782026PPNI) */
export function nombreImagenSugerido(fechaISO, clasificacion) {
  if (!fechaISO || !clasificacion) return '';
  const d = new Date(`${fechaISO}T00:00:00`);
  const inicio = new Date(d.getFullYear(), 0, 1);
  const dia = Math.round((d - inicio) / 86400000) + 1;
  return `${pad(dia, 3)}${d.getFullYear()}${clasificacion}`;
}

export const mesDe = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('es-MX', { month: 'long' }).replace(/^./, (c) => c.toUpperCase()) : '');

export function pctMuestra(kg, muestra) {
  const a = Number(kg);
  const b = Number(muestra);
  if (!kg || !muestra || Number.isNaN(a) || Number.isNaN(b) || b <= 0) return null;
  return (a / b) * 100;
}

/** Valores de una lista del catálogo: activos, más el valor actual aunque esté inactivo */
function opciones(catalogos, tipo, actual) {
  const lista = (catalogos?.listas?.[tipo] || []).filter((x) => x.activo).map((x) => x.valor);
  if (actual && !lista.includes(actual)) lista.unshift(actual);
  return lista;
}

/** Campos obligatorios que faltan (para avisar antes de guardar) */
export function faltantes(v) {
  return [
    !v.fecha_deteccion && 'Fecha de detección',
    !v.clasificacion && 'Clasificación',
    !v.producto && 'Nombre del producto',
    (v.kg_desviacion === '' || v.kg_desviacion === null || v.kg_desviacion === undefined) && 'Cantidad con la desviación (kg)',
  ].filter(Boolean);
}

/** Valores escritos en un campo de búsqueda que no están en su lista */
export function fueraDeLista(v, catalogos) {
  const malos = [];
  for (const [campo, tipo, etiqueta] of [['producto', 'producto', 'Nombre del producto'], ['proveedor', 'proveedor', 'Proveedor']]) {
    if (v[campo] && !opciones(catalogos, tipo).includes(v[campo])) malos.push(`${etiqueta}: "${v[campo]}"`);
  }
  return malos;
}

export default function PpniPncFormulario({ valores, onCambio, catalogos, intento, conDisposicion = true, s }) {
  const set = (campo) => (e) => {
    const valor = e.target.value;
    const nuevo = { ...valores, [campo]: valor };
    // Si no pertenece a un PCC, "¿Cuál PCC?" es N/A
    if (campo === 'pertenece_pcc' && valor && valor !== 'Sí') nuevo.cual_pcc = 'N/A';
    if (campo === 'pertenece_pcc' && valor === 'Sí' && valores.cual_pcc === 'N/A') nuevo.cual_pcc = '';
    onCambio(nuevo);
  };
  const vacio = (campo) => intento && (valores[campo] === '' || valores[campo] === null || valores[campo] === undefined);
  const estilo = (campo, obligatorio) => (obligatorio && vacio(campo) ? { ...s.input, ...x.faltante } : s.input);
  const pct = pctMuestra(valores.kg_desviacion, valores.total_muestra);
  const sugerido = nombreImagenSugerido(valores.fecha_deteccion, valores.clasificacion);
  const clasif = catalogos?.clasificaciones || [];

  const Select = ({ campo, lista, obligatorio, disabled }) => (
    <select value={valores[campo] || ''} onChange={set(campo)} style={estilo(campo, obligatorio)} disabled={disabled}>
      <option value="">— Selecciona —</option>
      {lista.map((o) => (typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.valor} value={o.valor}>{o.etiqueta}</option>))}
    </select>
  );

  return (
    <>
      <datalist id="ppni-productos">{opciones(catalogos, 'producto').map((p) => <option key={p} value={p} />)}</datalist>
      <datalist id="ppni-proveedores">{opciones(catalogos, 'proveedor').map((p) => <option key={p} value={p} />)}</datalist>

      <div style={x.seccion}>Detección</div>
      <div style={x.grid}>
        <Campo label="Fecha de detección *">
          <input type="date" value={valores.fecha_deteccion || ''} onChange={set('fecha_deteccion')} style={estilo('fecha_deteccion', true)} />
          {valores.fecha_deteccion && <span style={x.ayuda}>Mes: {mesDe(valores.fecha_deteccion)}</span>}
        </Campo>
        <Campo label="Turno de la desviación">
          {Select({ campo: 'turno', lista: catalogos?.turnos || ['Día', 'Noche'] })}
        </Campo>
        <Campo label="Área / proceso">
          {Select({ campo: 'area_proceso', lista: opciones(catalogos, 'area_proceso', valores.area_proceso) })}
        </Campo>
        <Campo label="¿Quién captura? (Rúbrica)">
          <input type="text" value={valores.quien_captura || ''} onChange={set('quien_captura')} style={s.input} maxLength={150} />
        </Campo>
      </div>

      <div style={x.seccion}>Clasificación</div>
      <div style={x.grid}>
        <Campo label="Clasificación *">
          {Select({ campo: 'clasificacion', lista: clasif.map((c) => ({ valor: c.valor, etiqueta: `${c.valor} — ${c.nombre}` })), obligatorio: true })}
        </Campo>
        <Campo label="Tipo de producto">
          {Select({ campo: 'tipo_producto', lista: opciones(catalogos, 'tipo_producto', valores.tipo_producto) })}
        </Campo>
        <Campo label="Tipo de desviación" ancho>
          {Select({ campo: 'tipo_desviacion', lista: opciones(catalogos, 'tipo_desviacion', valores.tipo_desviacion) })}
        </Campo>
        <Campo label="¿Pertenece a un PCC?">
          {Select({ campo: 'pertenece_pcc', lista: catalogos?.si_no_na || ['Sí', 'No', 'N/A'] })}
        </Campo>
        <Campo label="¿Cuál PCC?">
          {Select({ campo: 'cual_pcc', lista: opciones(catalogos, 'cual_pcc', valores.cual_pcc), disabled: valores.pertenece_pcc && valores.pertenece_pcc !== 'Sí' })}
        </Campo>
        <Campo label="¿Peligro físico? (mayor a 7 mm)">
          {Select({ campo: 'peligro_fisico', lista: catalogos?.si_no_na || ['Sí', 'No', 'N/A'] })}
        </Campo>
      </div>

      <div style={x.seccion}>Producto</div>
      <div style={x.grid}>
        <Campo label="Nombre del producto *" ancho>
          <input type="text" list="ppni-productos" placeholder="Escribe para buscar…" value={valores.producto || ''} onChange={set('producto')} style={estilo('producto', true)} />
        </Campo>
        <Campo label="Proveedor" ancho>
          <input type="text" list="ppni-proveedores" placeholder="Escribe para buscar…" value={valores.proveedor || ''} onChange={set('proveedor')} style={s.input} />
        </Campo>
        <Campo label="No. Lote">
          <input type="text" value={valores.lote || ''} onChange={set('lote')} style={s.input} />
        </Campo>
        <Campo label="Número de NetSuite">
          <input type="text" value={valores.numero_netsuite || ''} onChange={set('numero_netsuite')} style={s.input} placeholder="Traslado (vacío si no aplica)" />
        </Campo>
      </div>

      <div style={x.seccion}>Cantidades</div>
      <div style={x.grid}>
        <Campo label="Cantidad con la desviación (kg) *">
          <input type="number" min="0" step="0.001" value={valores.kg_desviacion ?? ''} onChange={set('kg_desviacion')} style={estilo('kg_desviacion', true)} />
        </Campo>
        <Campo label="Total de muestra (kg)">
          <input type="number" min="0" step="0.001" value={valores.total_muestra ?? ''} onChange={set('total_muestra')} style={s.input} />
        </Campo>
        <Campo label="% que representa">
          <div style={{ ...s.input, background: '#F3FAF5', fontWeight: 700, color: pct !== null && pct > 100 ? '#B23A48' : '#2A1830' }}>
            {pct === null ? '—' : `${pct.toFixed(1)}%`}
          </div>
          {pct !== null && pct > 100 && <span style={{ ...x.ayuda, color: '#B23A48' }}>La cantidad con desviación es mayor que la muestra.</span>}
        </Campo>
      </div>

      <div style={x.seccion}>Descripción</div>
      <textarea value={valores.descripcion || ''} onChange={set('descripcion')} style={{ ...s.input, minHeight: 70, resize: 'vertical' }} placeholder="Describe la desviación" />

      <div style={x.seccion}>Imagen de referencia (SharePoint)</div>
      <div style={x.grid}>
        <Campo label="Nombre de la imagen">
          <input type="text" value={valores.imagen_nombre || ''} onChange={set('imagen_nombre')} style={s.input} placeholder={sugerido || 'Ej. 2782026PPNI'} />
          {sugerido && !valores.imagen_nombre && (
            <button type="button" style={x.link} onClick={() => onCambio({ ...valores, imagen_nombre: sugerido })}>Usar {sugerido}</button>
          )}
        </Campo>
        <Campo label="Liga de la imagen en SharePoint" ancho>
          <input type="url" value={valores.imagen_link || ''} onChange={set('imagen_link')} style={s.input} placeholder="https://dasavenagourmet123.sharepoint.com/…" />
          <span style={x.ayuda}>
            Guarda la imagen en la carpeta "Seguimiento a PPNI y PNC / Imágenes de referencia" y pega aquí su liga.{' '}
            <a href={LIGA_CARPETA_IMAGENES} target="_blank" rel="noreferrer" style={{ color: '#4A1E5C', fontWeight: 600 }}>Abrir carpeta <FiExternalLink size={10} /></a>
          </span>
        </Campo>
      </div>

      {conDisposicion && (
        <>
          <div style={x.seccion}>Disposición</div>
          <div style={x.grid}>
            <Campo label="Disposición">
              {Select({ campo: 'disposicion', lista: opciones(catalogos, 'disposicion', valores.disposicion) })}
              {!valores.disposicion && <span style={x.ayuda}>Si todavía no se decide, déjala vacía: el registro queda en Pendientes de disposición.</span>}
            </Campo>
            {valores.disposicion && (
              <Campo label="Autorizó (firma)">
                <input type="text" value={valores.firma_disposicion || ''} onChange={set('firma_disposicion')} style={s.input} placeholder="Si la dejas vacía, se pone tu usuario" />
              </Campo>
            )}
          </div>
        </>
      )}
    </>
  );
}

const Campo = ({ label, children, ancho }) => (
  <label style={{ ...x.label, ...(ancho ? { gridColumn: '1 / -1' } : {}) }}>
    {label}
    {children}
  </label>
);

const x = {
  seccion: { fontSize: 11, fontWeight: 700, color: '#6E4F80', textTransform: 'uppercase', letterSpacing: 0.5, margin: '18px 0 8px', borderBottom: '1px solid #F0E9E0', paddingBottom: 4 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, color: '#6E4F80', fontWeight: 600, minWidth: 0 },
  ayuda: { fontSize: 11, color: '#8A7690', fontWeight: 400 },
  faltante: { border: '2px solid #B23A48', background: '#FDF3F1' },
  link: { background: 'none', border: 'none', padding: 0, fontSize: 11, color: '#4A1E5C', textDecoration: 'underline', cursor: 'pointer', textAlign: 'left' },
};